import { apiFetch } from "@/apis/http/apiFetch";
import { resolveAuthHeaderValue } from "@/apis/http/authHeaders";
import { agentConfig } from "@/shared/config/agentConfig";

/**
 * REST surface of the Tower.Agent service (history + feedback, #671/#673, and
 * the user's saved memories).
 * Plain requests go through apiFetch (which already handles auth headers and
 * 401 renew-retry, and accepts absolute URLs); the streaming chat run lives in
 * ./agentStream instead because it speaks AG-UI over SSE.
 */

export interface ConversationSummary {
  id: string;
  title: string;
  createdAtUtc: string;
  lastActivityUtc: string;
  turnCount: number;
  /**
   * TEMPORARY (mock-only): one-line preview under the title. The backend does
   * not return this yet; it is currently fed by the dev agent mock. Remove the
   * optionality (or drop the field) once the agent API's contract includes it.
   */
  preview?: string;
}

export interface ConversationList {
  items: ConversationSummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ToolCallSummary {
  name: string;
  durationMs: number;
  error: string | null;
}

export interface AgentTurn {
  seq: number;
  role: string;
  text: string | null;
  contentBlocksJson: string | null;
  toolCalls: ToolCallSummary[];
  error: string | null;
  feedbackRating: string | null;
  feedbackComment: string | null;
  createdAtUtc: string;
}

export interface TurnList {
  items: AgentTurn[];
  page: number;
  pageSize: number;
  total: number;
}

export type AgentTaskKind = "daily" | "alert" | "periodic" | "weekly";

export interface AgentTask {
  id: string;
  conversationId: string;
  instruction: string;
  fireAtUtc: string;
  status: string;
  createdAtUtc: string;
  completedAtUtc: string | null;
  error: string | null;
  // TEMPORARY (mock-only): the fields below are not part of the agent API
  // contract yet — they are populated by the dev agent mock to drive the
  // landing/scheduled cards. Fold them into the real contract (and drop the
  // optionality) once the backend returns them.
  /** Short card title; falls back to instruction when absent. */
  title?: string;
  /** One-line description under the title. */
  description?: string;
  /** Cadence/kind chip on the landing cards; defaults to periodic. */
  kind?: AgentTaskKind;
}

export interface AgentTaskList {
  items: AgentTask[];
  page: number;
  pageSize: number;
  total: number;
}

export interface AgentUsage {
  usedTokens: number;
  /** 0 means no budget is enforced. */
  budgetTokens: number;
}

/** A fact or preference the agent remembers about the signed-in user across conversations. */
export interface UserMemory {
  id: string;
  text: string;
  /** The conversation the memory was saved (or last updated) from. */
  sourceConversationId: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface UserMemoryList {
  /** Most recently updated first; unpaged because the per-user cap bounds it. */
  items: UserMemory[];
  maxMemories: number;
}

function agentUrl(path: string): string {
  return `${agentConfig.baseUrl}${path}`;
}

/**
 * Headers for the AG-UI streaming transport, which manages its own requests.
 * Re-resolved before every run so a silently renewed token is picked up
 * (spike finding, #668). In dev builds without a signed-in user, the dev-mode
 * agent service authenticates via the X-Dev-User-Id header instead.
 */
export async function getAgentAuthHeaders(): Promise<Record<string, string>> {
  const authorization = await resolveAuthHeaderValue();
  if (authorization) {
    return { Authorization: authorization };
  }

  return import.meta.env.DEV ? { "X-Dev-User-Id": "dev-user" } : {};
}

/** Probes the agent service; false means the drawer shows its unavailable state. */
export async function fetchAgentAvailable(): Promise<boolean> {
  try {
    const response = await apiFetch(agentUrl("/api/v1/agent/status"), {
      suppressAuthRedirect: true,
      allow404: true,
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Lists the caller's active conversations, or the archived ones when `archived` is true. */
export async function listConversations(
  page = 1,
  pageSize = 20,
  archived = false,
): Promise<ConversationList> {
  const archivedParam = archived ? "&archived=true" : "";
  const response = await apiFetch(
    agentUrl(`/api/v1/agent/conversations?page=${page}&pageSize=${pageSize}${archivedParam}`),
  );
  return (await response.json()) as ConversationList;
}

/** Hides a conversation from the active history; its turns are kept server-side. */
export async function archiveConversation(conversationId: string): Promise<void> {
  await apiFetch(
    agentUrl(`/api/v1/agent/conversations/${encodeURIComponent(conversationId)}/archive`),
    {
      method: "PUT",
    },
  );
}

export async function unarchiveConversation(conversationId: string): Promise<void> {
  await apiFetch(
    agentUrl(`/api/v1/agent/conversations/${encodeURIComponent(conversationId)}/archive`),
    {
      method: "DELETE",
    },
  );
}

export async function getTurns(
  conversationId: string,
  page = 1,
  pageSize = 100,
): Promise<TurnList> {
  const response = await apiFetch(
    agentUrl(
      `/api/v1/agent/conversations/${encodeURIComponent(conversationId)}/turns?page=${page}&pageSize=${pageSize}`,
    ),
  );
  return (await response.json()) as TurnList;
}

export async function putTurnFeedback(
  conversationId: string,
  seq: number,
  rating: "up" | "down" | null,
  comment: string | null,
): Promise<void> {
  await apiFetch(
    agentUrl(
      `/api/v1/agent/conversations/${encodeURIComponent(conversationId)}/turns/${seq}/feedback`,
    ),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rating, comment }),
    },
  );
}

export async function fetchAgentUsage(): Promise<AgentUsage> {
  const response = await apiFetch(agentUrl("/api/v1/agent/usage"));
  return (await response.json()) as AgentUsage;
}

export async function listTasks(page = 1, pageSize = 20): Promise<AgentTaskList> {
  const response = await apiFetch(
    agentUrl(`/api/v1/agent/tasks?page=${page}&pageSize=${pageSize}`),
  );
  return (await response.json()) as AgentTaskList;
}

export async function cancelAgentTask(taskId: string): Promise<void> {
  await apiFetch(agentUrl(`/api/v1/agent/tasks/${encodeURIComponent(taskId)}`), {
    method: "DELETE",
  });
}

export async function listMemories(): Promise<UserMemoryList> {
  const response = await apiFetch(agentUrl("/api/v1/agent/memories"));
  return (await response.json()) as UserMemoryList;
}

export async function deleteMemory(memoryId: string): Promise<void> {
  await apiFetch(agentUrl(`/api/v1/agent/memories/${encodeURIComponent(memoryId)}`), {
    method: "DELETE",
  });
}

export async function deleteAllMemories(): Promise<void> {
  await apiFetch(agentUrl("/api/v1/agent/memories"), { method: "DELETE" });
}
